<template>
    <div class="parameter-validation-form">
        <div class="entity-picker-section">
            <span class="sectionTitle">{{ $t("chatbotv2.parameterValidation") }}</span>
            <a-popover v-model="entityPickerVisible" trigger="click" :overlayClassName="'no-padding'" placement="left">
                <div slot="content">
                    <ValidationEntityPicker
                        :default-selected="clone.validationType"
                        :pick="onValidationEntityPicked"
                        :omit="omit"
                    />
                </div>
                <div class="entity-input">
                    <div class="entity-input-text-wrapper">
                        <div class="entity-input-placeholder" v-if="$_.isEmpty(clone.validationType)">
                            {{ $t("chatbotv2.selectReplyType") }}
                        </div>
                        <div class="single-entity-style" v-else :class="{ active: entityPickerVisible }">
                            <span>
                                {{
                                    `${getValidationEntity(clone.validationType).value}`.startsWith("akinon_") ||
                                    Attributes.includes(getValidationEntity(clone.validationType).value)
                                        ? $t(`chatbotv2.attr.${getValidationEntity(clone.validationType).value}`)
                                        : Entities.includes(getValidationEntity(clone.validationType).value)
                                        ? $t(`chatbotv2.${getValidationEntity(clone.validationType).value}`)
                                        : getValidationEntity(clone.validationType).value
                                }}
                            </span>
                        </div>
                    </div>
                    <span class="material-icons" v-if="!$_.isEmpty(clone.validationType)"> expand_more </span>
                </div>
            </a-popover>
        </div>
        <div class="attribute-picker-section">
            <span class="sectionTitle"> {{ $t("chatbotv2.saveToAttribute") }}</span>
            <a-popover
                v-model="attributePickerVisible"
                trigger="click"
                :overlayClassName="'no-padding'"
                placement="left"
            >
                <div slot="content">
                    <AttributePicker :default-selected="clone.attributeName" :pick="onAttributePicked" />
                </div>
                <div class="attribute-input">
                    <div class="attribute-input-text-wrapper">
                        <div class="attribute-input-placeholder" v-if="$_.isEmpty(clone.attributeName)">
                            {{ $t("chatbotv2.chooseAttributeType") }}
                        </div>
                        <div class="single-attribute-style" v-else :class="{ active: attributePickerVisible }">
                            <AttributeIconPreview :attribute="clone.attributeName" />
                            <span>
                                {{
                                    `${clone.attributeName}`.startsWith("akinon_") ||
                                    Attributes.includes(clone.attributeName)
                                        ? $t(`chatbotv2.attr.${clone.attributeName}`)
                                        : Entities.includes(clone.attributeName)
                                        ? $t(`chatbotv2.${clone.attributeName}`)
                                        : clone.attributeName.startsWith("contact_property_")
                                        ? visitorContactAttributes[clone.attributeName.replace("contact_property_", "")]
                                        : clone.attributeName
                                }}
                            </span>
                        </div>
                    </div>
                    <span class="material-icons" v-if="!$_.isEmpty(clone.attributeName)"> expand_more </span>
                </div>
            </a-popover>
        </div>
        <div class="buttons-wrapper">
            <a-button size="large" @click="onCancel">{{ $t("message.cancel") }}</a-button>
            <a-button size="large" type="primary" @click="save" :disabled="!isChanged()"
                >{{ $t("message.save") }}
            </a-button>
        </div>
    </div>
</template>

<script>
import ValidationEntityPicker from "../../pickers/ValidationEntityPicker";
import log2 from "../../../../../../../utils/log2";
import { EntityTypes } from "../../../../palatteEnums";
import SYSTEM_PUBSUB from "../../../../../../../utils/systemPubSub";
import AttributeIconPreview from "../AttributeIconPreview";
import AttributePicker from "../../pickers/AttributePicker";
import { Attributes, Entities } from "../../../../palatteEnums";
import { mapState } from "vuex";
import TiptapEditor from "../TiptapEditor.vue";

const log = log2("ParameterValidationForm");
export default {
    name: "ParameterValidationForm",
    components: {
        TiptapEditor,
        ValidationEntityPicker,
        AttributeIconPreview,
        AttributePicker,
    },
    props: {
        attributeName: {
            type: String,
        },
        validationType: {
            type: String,
        },
        onChanged: {
            type: Function,
            default: () => {},
        },
        onCancel: {
            type: Function,
            default: () => {},
        },
        omit: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    data() {
        return {
            clone: {
                attributeName: this.attributeName,
                validationType: this.validationType,
            },
            entityPickerVisible: false,
            attributePickerVisible: false,
            Attributes,
            Entities,
        };
    },
    computed: {
        ...mapState({
            visitorContactAttributes: (state) =>
                (state.contactProperty.data || []).reduce((a, b) => {
                    a[b._id] = b.label;
                    return a;
                }, {}),
        }),
    },
    methods: {
        save() {
            this.onChanged(this.clone);
        },
        isChanged() {
            const initialObj = {
                attributeName: this.attributeName,
                validationType: this.validationType,
            };

            return !_.isEqual(initialObj, this.clone);
        },
        onValidationEntityPicked(entity) {
            log("onValidationEntityPicked", entity);
            this.clone.validationType = entity;
            this.entityPickerVisible = false;
        },
        getValidationEntity(entity) {
            if (entity.startsWith("user_")) {
                return {
                    value: entity,
                    name: entity.replace("user_", ""),
                    description: "Custom entries",
                };
            }
            return Object.keys(EntityTypes)
                .map((key) => EntityTypes[key])
                .find((entityType) => entityType.value === entity);
        },
        onAttributePicked(newAttribute) {
            this.clone.attributeName = newAttribute;
            this.active = false;
            // log("CB_ATTRIBUTE_CHANGED event sending", this.node.attrs["data-id"]);
            // SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.CB_ATTRIBUTE_CHANGED, {
            //     attributeId: this.node.attrs["data-id"],
            //     name: newAttribute,
            // });
        },
    },
    watch: {},
};
</script>

<style lang="scss">
.parameter-validation-form {
    padding: 25px 20px;
    width: 340px;

    .labelTitle {
        margin-bottom: 10px;
        color: #22272d;
        font-size: 16px;
        line-height: 20px;
    }

    .entity-picker-section {
        margin-top: 20px;

        .sectionTitle {
            margin-bottom: 10px;
            margin-top: 15px;
            color: #22272d;
            font-weight: 600;
            font-size: 16px;
            line-height: 20px;
        }

        .entity-input {
            align-items: center;
            background: #fff;
            border: 1px solid #c6cdd4;
            border-radius: 6px;
            cursor: pointer;
            display: flex;
            height: 43px;
            padding: 15px;
            position: relative;
            transition: all 0.3s;
            user-select: none;
            width: 100%;
            margin-top: 10px;

            &:hover {
                border: 1px solid #006cff !important;
            }

            .entity-input-text-wrapper {
                display: flex;
                flex-grow: 1;
                width: 0;
            }

            .entity-input-placeholder {
                color: #9ba6b3;
                font-size: 15px;
                line-height: 19px;
                overflow: hidden !important;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .single-entity-style {
                //background: #ffdc66;
                cursor: pointer;
                transition: all 0.3s;
                //border-radius: 10px;
                box-decoration-break: clone;
                padding: 0 8px;
                display: flex;
                justify-content: center;
                align-items: center;
                font-weight: 800;

                &.active {
                    background: #006cff;
                    color: #fff;

                    .material-icons {
                        color: #fff;
                    }
                }

                .material-icons {
                    color: #647283;
                    font-size: 18px;
                    margin-right: 3px;
                }
            }
        }
    }

    .attribute-picker-section {
        margin-top: 20px;

        .sectionTitle {
            margin-bottom: 10px;
            margin-top: 15px;
            color: #22272d;
            font-weight: 600;
            font-size: 16px;
            line-height: 20px;
        }

        .attribute-input {
            align-items: center;
            background: #fff;
            border: 1px solid #c6cdd4;
            border-radius: 6px;
            cursor: pointer;
            display: flex;
            height: 43px;
            padding: 15px;
            position: relative;
            transition: all 0.3s;
            user-select: none;
            width: 100%;

            &:hover {
                border: 1px solid #006cff !important;
            }

            .attribute-input-text-wrapper {
                display: flex;
                flex-grow: 1;
                width: 0;
            }

            .attribute-input-placeholder {
                color: #9ba6b3;
                font-size: 15px;
                line-height: 19px;
                overflow: hidden !important;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .single-attribute-style {
                background: #ffdc66;
                cursor: pointer;
                transition: all 0.3s;
                border-radius: 10px;
                box-decoration-break: clone;
                padding: 0 8px;
                display: flex;
                justify-content: center;
                align-items: center;
                font-weight: 800;

                &.active {
                    background: #006cff;
                    color: #fff;

                    .material-icons {
                        color: #fff;
                    }
                }

                .material-icons {
                    color: #647283;
                    font-size: 18px;
                    margin-right: 3px;
                }
            }
        }
    }

    .buttons-wrapper {
        display: flex;
        justify-content: flex-end;
        margin-top: 20px;
        gap: 20px;
    }
}
</style>
